import type {
	CatalogResponseModel,
	PricingRuleListParams,
	PricingRuleListResponseModel,
	PricingRuleModel,
	PricingRuleVersionListResponseModel,
	PricingRuleVersionModel,
	PricingRuleVersionsListParams,
} from '@/shared/generated'
import type { UseQueryOptions } from '@/shared/lib/query-client'
import type { AxiosError } from 'axios'

import { useQuery } from '@tanstack/react-query'

import { client } from '@/shared/api/client'

export const pricingRuleQueries = {
	all: () => ({ queryKey: ['pricing-rules'] }),
	list: (params: PricingRuleListParams = {}) => ({
		queryKey: ['pricing-rules', 'list', params],
		queryFn: () => client.pricingRule.pricingRuleList(params),
	}),
	single: (id: string) => ({
		queryKey: ['pricing-rules', 'detail', id],
		queryFn: () => client.pricingRule.pricingRuleGet({ pricingRuleId: id }),
	}),
	draft: (id: string) => ({
		queryKey: ['pricing-rules', 'draft', id],
		queryFn: () =>
			client.pricingRule.pricingRuleDraftGet({ pricingRuleId: id }),
	}),
	versions: (
		id: string,
		params: Omit<PricingRuleVersionsListParams, 'pricingRuleId'> = {}
	) => ({
		queryKey: ['pricing-rules', 'versions', id, params],
		queryFn: () =>
			client.pricingRule.pricingRuleVersionsList({
				pricingRuleId: id,
				...params,
			}),
	}),
	version: (id: string, vid: string) => ({
		queryKey: ['pricing-rules', 'versions', id, 'detail', vid],
		queryFn: () =>
			client.pricingRule.pricingRuleVersionGet({
				pricingRuleId: id,
				pricingRuleVersionId: vid,
			}),
	}),
	catalog: () => ({
		queryKey: ['pricing-rules', 'catalog'],
		queryFn: () => client.pricingRule.pricingRuleCatalog(),
	}),
	exportDraft: (pricingRuleId: string) => ({
		queryKey: ['pricing-rules', 'export', pricingRuleId],
		queryFn: () => client.pricingRule.pricingRuleDraftExport({ pricingRuleId }),
	}),
}

export function usePricingRulesQuery<T = PricingRuleListResponseModel>(
	params: PricingRuleListParams = {},
	options?: UseQueryOptions<PricingRuleListResponseModel, Error, T>
) {
	return useQuery<PricingRuleListResponseModel, Error, T>({
		...pricingRuleQueries.list(params),
		...(options || {}),
	})
}

export function usePricingRuleCatalogQuery<T = CatalogResponseModel>(
	options?: UseQueryOptions<CatalogResponseModel, Error, T>
) {
	return useQuery<CatalogResponseModel, Error, T>({
		...pricingRuleQueries.catalog(),
		...(options || {}),
	})
}
export function usePricingRuleQuery<T = PricingRuleModel>(
	id: string,
	options?: UseQueryOptions<PricingRuleModel, Error, T>
) {
	return useQuery<PricingRuleModel, Error, T>({
		...pricingRuleQueries.single(id),
		enabled: !!id,
		...(options || {}),
	})
}

export function usePricingRuleDraftQuery<T = PricingRuleVersionModel>(
	id: string,
	options?: UseQueryOptions<PricingRuleVersionModel, AxiosError, T>
) {
	return useQuery<PricingRuleVersionModel, AxiosError, T>({
		...pricingRuleQueries.draft(id),
		enabled: !!id,
		...(options || {}),
	})
}

export function usePricingRuleVersionsQuery<
	T = PricingRuleVersionListResponseModel,
>(
	id: string,
	params: Omit<PricingRuleVersionsListParams, 'pricingRuleId'> = {},
	options?: UseQueryOptions<PricingRuleVersionListResponseModel, AxiosError, T>
) {
	return useQuery<PricingRuleVersionListResponseModel, AxiosError, T>({
		...pricingRuleQueries.versions(id, params),
		enabled: !!id,
		...(options || {}),
	})
}

export function usePricingRuleVersionQuery<T = PricingRuleVersionModel>(
	id: string,
	vid: string,
	options?: UseQueryOptions<PricingRuleVersionModel, AxiosError, T>
) {
	return useQuery<PricingRuleVersionModel, AxiosError, T>({
		...pricingRuleQueries.version(id, vid),
		enabled: !!id && !!vid,
		...(options || {}),
	})
}

export function usePricingRuleDraftExportQuery<T = any>(
	pricingRuleId: string,
	options?: UseQueryOptions<any, AxiosError, T>
) {
	return useQuery<any, AxiosError, T>({
		...pricingRuleQueries.exportDraft(pricingRuleId),
		refetchOnMount: 'always',
		...(options || {}),
	})
}
